import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client';
import { btn } from '../components/FilterBar';

/**
 * SOP — the document library. Ports frmSOP and frmDescription.
 *
 * Not a database screen: the desktop fetched a folder listing from
 * listFiles.php on the membership host and rendered the .docx files it
 * returned. The API proxies that call, since the host sets no CORS headers.
 */

interface Folder {
  name: string;
  hasSubfolders: boolean;
}

interface Listing {
  folder: string;
  subfolders: string[];
  files: { name: string; url: string }[];
}

export default function Sop() {
  const [folder, setFolder] = useState<string | null>(null);
  const [subfolder, setSubfolder] = useState<string | null>(null);

  const foldersQuery = useQuery({
    queryKey: ['sop-folders'],
    queryFn: () => apiGet<{ folders: Folder[] }>('/sop'),
  });

  const active = foldersQuery.data?.folders.find((f) => f.name === folder);
  const path = subfolder ? `${folder}/${subfolder}` : folder;

  const listingQuery = useQuery({
    queryKey: ['sop-files', path, active?.hasSubfolders, subfolder],
    queryFn: () =>
      apiGet<Listing>('/sop/files', {
        folder: path,
        // Subfolders are only listed at the top level, matching how the desktop
        // switched the flag off once it navigated in.
        subFolder: subfolder ? 'false' : String(active?.hasSubfolders ?? false),
      }),
    enabled: Boolean(path),
    retry: false,
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">SOP</h1>
        <p className="text-sm text-muted">Dokumentet e procedurave sipas rolit</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[16rem_1fr]">
        <nav className="rounded-xl bg-white p-3">
          <h2 className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted">
            Dosjet
          </h2>
          <ul className="space-y-0.5">
            {(foldersQuery.data?.folders ?? []).map((f) => (
              <li key={f.name}>
                <button
                  type="button"
                  onClick={() => {
                    setFolder(f.name);
                    setSubfolder(null);
                  }}
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                    folder === f.name
                      ? 'bg-brand-500 font-medium text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {f.name}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <section className="rounded-xl bg-white p-4">
          {!folder && (
            <p className="py-10 text-center text-sm text-muted">
              Zgjidhni një dosje nga lista majtas.
            </p>
          )}

          {folder && (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <h2 className="font-semibold">{folder}</h2>
                {subfolder && (
                  <>
                    <span className="text-muted">/</span>
                    <span className="font-medium">{subfolder}</span>
                    <button
                      type="button"
                      onClick={() => setSubfolder(null)}
                      className="ml-2 text-xs font-medium text-brand-600 hover:underline"
                    >
                      ← Kthehu
                    </button>
                  </>
                )}
              </div>

              {listingQuery.isLoading && (
                <p className="py-10 text-center text-sm text-muted">Duke ngarkuar…</p>
              )}

              {listingQuery.isError && (
                <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  Serveri i dokumenteve nuk u përgjigj. Dokumentet ruhen jashtë këtij sistemi.
                </div>
              )}

              {listingQuery.data && (
                <>
                  {listingQuery.data.subfolders.length > 0 && (
                    <ul className="mb-4 space-y-1">
                      {listingQuery.data.subfolders.map((s) => (
                        <li key={s}>
                          <button
                            type="button"
                            onClick={() => setSubfolder(s)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-left text-sm font-medium hover:border-slate-300 hover:bg-slate-50"
                          >
                            📁 {s}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {listingQuery.data.files.length === 0 &&
                    listingQuery.data.subfolders.length === 0 && (
                      <p className="py-10 text-center text-sm text-muted">
                        Kjo dosje është bosh.
                      </p>
                    )}

                  <ul className="space-y-1">
                    {listingQuery.data.files.map((f) => (
                      <li
                        key={f.name}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2"
                      >
                        <span className="min-w-0 flex-1 truncate text-sm">📄 {f.name}</span>
                        <a
                          href={f.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className={btn.secondary}
                        >
                          Hap
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
